import React, { useState, useEffect, useTransition, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Search, Users, Calendar, MessageSquare, MapPin, Sparkles,
    ChevronRight, Briefcase, GraduationCap, Home, User, Building2,
    CalendarDays, Star, TrendingUp
} from 'lucide-react';
import { getAllPeople } from '../../services/apiService';
import { getUserContacts } from '../../services/userContactsService';
import { PeopleCard } from '../people';
import { useTranslation } from '../../context/LanguageContext';
import { getStoredUser } from '../../context/AuthContext';
import { useTheme } from '../../context/ThemeContext';

// Profile types mapping
const PROFILE_TYPES = {
    1: { code: 'STUDENT', labelFr: 'Étudiant', labelEn: 'Student', icon: GraduationCap, color: 'bg-blue-500' },
    3: { code: 'INDIVIDUAL', labelFr: 'Particulier', labelEn: 'Individual', icon: Home, color: 'bg-gray-500' },
    4: { code: 'ARTISAN', labelFr: 'Artisan', labelEn: 'Artisan', icon: Building2, color: 'bg-amber-500' },
    5: { code: 'PROFESSIONAL', labelFr: 'Professionnel', labelEn: 'Professional', icon: Briefcase, color: 'bg-indigo-500' },
    6: { code: 'RETIRED', labelFr: 'Pensionné', labelEn: 'Retired', icon: User, color: 'bg-teal-500' }
};

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://apicabaza.creago.be/rest/cabaza';

/**
 * Dashboard - Home page after login with stats and discovery
 */
const Dashboard = ({ onNavigate }) => {
    const navigate = useNavigate();
    const { t, language } = useTranslation();
    const { currentTheme } = useTheme();
    const user = getStoredUser();
    const userId = user?.id || parseInt(localStorage.getItem('userId'), 10);
    const userEmail = user?.email || localStorage.getItem('userEmail') || '';

    // Stats
    const [stats, setStats] = useState({
        contacts: 0,
        eventsCreated: 0,
        eventsParticipating: 0,
        conversations: 0,
        favoriteSpots: 0
    });

    // Upcoming events
    const [upcomingEvents, setUpcomingEvents] = useState([]);

    // Discovery profile
    const [discoveryProfile, setDiscoveryProfile] = useState(null);

    // Search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [allPeople, setAllPeople] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [userContacts, setUserContacts] = useState([]);
    const [contactPeopleIds, setContactPeopleIds] = useState(new Set());

    const [isPending, startTransition] = useTransition();
    const deferredSearchTerm = useDeferredValue(searchTerm);

    // Loading states
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        loadDashboardData();
    }, [userId]);

    // Search effect
    useEffect(() => {
        if (deferredSearchTerm.trim().length >= 2) {
            setIsSearching(true);
            startTransition(() => {
                const searchLower = deferredSearchTerm.toLowerCase();
                const filtered = allPeople.filter(person => {
                    const firstName = (person.firstName || person.FIRSTNAME || '').toLowerCase();
                    const lastName = (person.lastName || person.LASTNAME || '').toLowerCase();
                    const email = (person.email || person.EMAIL || '').toLowerCase();
                    const company = (person.company || person.COMPANY || '').toLowerCase();
                    const occupation = (person.occupationName || person.occupation || '').toLowerCase();

                    return firstName.includes(searchLower) ||
                        lastName.includes(searchLower) ||
                        email.includes(searchLower) ||
                        company.includes(searchLower) ||
                        occupation.includes(searchLower);
                });
                setSearchResults(filtered.slice(0, 12));
            });
        } else {
            setIsSearching(false);
            setSearchResults([]);
        }
    }, [deferredSearchTerm, allPeople]);

    const loadDashboardData = async () => {
        setLoading(true);
        try {
            // Load all data in parallel
            const [contactsData, peopleData, eventsData, messagesData, placesData] = await Promise.all([
                loadContacts(),
                loadPeople(),
                loadEvents(),
                loadMessages(),
                loadFavoritePlaces()
            ]);

            // Set stats
            setStats({
                contacts: contactsData.count,
                eventsCreated: eventsData.created,
                eventsParticipating: eventsData.participating,
                conversations: messagesData.count,
                favoriteSpots: placesData.count
            });

            setUpcomingEvents(eventsData.upcoming);
            setUserContacts(contactsData.contacts);
            setContactPeopleIds(new Set(contactsData.contacts.map(c => c.peopleuuid).filter(Boolean)));
            setAllPeople(peopleData.people);

            // Pick a random discovery profile
            if (peopleData.people.length > 0) {
                const randomIndex = Math.floor(Math.random() * Math.min(peopleData.people.length, 20));
                setDiscoveryProfile(peopleData.people[randomIndex]);
            }

        } catch (error) {
            console.error('Error loading dashboard data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadContacts = async () => {
        try {
            const contacts = await getUserContacts(userId, true);
            return { count: contacts.length, contacts };
        } catch (error) {
            console.error('Error loading contacts:', error);
            return { count: 0, contacts: [] };
        }
    };

    const loadPeople = async () => {
        try {
            const data = await getAllPeople({ limit: 100, sortBy: 'createdAt', sortOrder: 'DESC' });
            const publicPeople = data.filter(p => {
                const role = p.role || p.ROLE;
                const email = p.email || p.EMAIL;
                return role === 'PUBLIC' && email !== userEmail;
            });
            return { people: publicPeople };
        } catch (error) {
            console.error('Error loading people:', error);
            return { people: [] };
        }
    };

    const loadEvents = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/events?userId=${userId}`);
            const result = await response.json();
            const events = result.data || result.DATA || [];

            const now = new Date();
            const upcoming = events
                .filter(e => new Date(e.startDate || e.start_date) > now)
                .sort((a, b) => new Date(a.startDate || a.start_date) - new Date(b.startDate || b.start_date))
                .slice(0, 3);

            const created = events.filter(e => (e.createdById || e.created_by_id) === userId).length;
            const participating = events.filter(e => {
                const participants = e.participants || [];
                return participants.some(p => p.userId === userId || p.user_id === userId);
            }).length;

            return { created, participating, upcoming };
        } catch (error) {
            console.error('Error loading events:', error);
            return { created: 0, participating: 0, upcoming: [] };
        }
    };

    const loadMessages = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/messages/conversations?userId=${userId}`);
            const result = await response.json();
            const conversations = result.data || result.DATA || [];
            return { count: conversations.length };
        } catch (error) {
            console.error('Error loading messages:', error);
            return { count: 0 };
        }
    };

    const loadFavoritePlaces = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/places/favorites?userId=${userId}`);
            const result = await response.json();
            const places = result.data || result.DATA || [];
            return { count: places.length };
        } catch (error) {
            console.error('Error loading favorite places:', error);
            return { count: 0 };
        }
    };

    const handlePeopleClick = (person) => {
        const peopleUuid = person.peopleUuid || person.PEOPLEUUID;
        navigate(`/people/${peopleUuid}`);
    };

    const isContactAdded = (peopleUuid) => {
        return contactPeopleIds.has(peopleUuid);
    };

    const formatEventDate = (dateStr) => {
        const date = new Date(dateStr);
        return date.toLocaleDateString(language === 'fr' ? 'fr-FR' : 'en-US', {
            weekday: 'short',
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Stat card component
    const StatCard = ({ icon: Icon, label, value, color, onClick }) => (
        <div
            onClick={onClick}
            className={`bg-white rounded-xl p-4 shadow-sm border border-gray-100 hover:shadow-md transition-all cursor-pointer group`}
        >
            <div className="flex items-center gap-3">
                <div className={`w-12 h-12 rounded-xl ${color} flex items-center justify-center`}>
                    <Icon className="w-6 h-6 text-white" />
                </div>
                <div className="flex-1">
                    <p className="text-2xl font-bold text-gray-900">{value}</p>
                    <p className="text-sm text-gray-500">{label}</p>
                </div>
                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[var(--color-cabaza-purple)] transition-colors" />
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="w-12 h-12 border-4 border-[var(--color-cabaza-purple)] border-t-transparent rounded-full animate-spin"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Welcome & Search */}
            <div className="rounded-2xl p-6 text-white" style={{ background: `linear-gradient(to right, var(--color-cabaza-purple), var(--color-cabaza-pink))` }}>
                <h1 className="text-2xl font-bold mb-2">
                    {t.dashboard?.welcome || 'Bienvenue'}, {user?.firstName || 'Utilisateur'} !
                </h1>
                <p className="text-white/80 mb-4">
                    {t.dashboard?.welcomeSubtitle || 'Découvrez votre réseau et connectez-vous avec de nouvelles personnes'}
                </p>

                {/* Search bar */}
                <div className="relative">
                    <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder={t.dashboard?.searchPlaceholder || 'Rechercher un profil, une entreprise, un métier...'}
                        className="w-full pl-12 pr-4 py-3 rounded-xl bg-white text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-white/50"
                    />
                </div>
            </div>

            {/* Search Results */}
            {isSearching && (
                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <h2 className="text-lg font-semibold text-gray-900">
                            {t.dashboard?.searchResults || 'Résultats de recherche'}
                            <span className="ml-2 text-sm font-normal text-gray-500">
                                ({searchResults.length} {searchResults.length > 1 ? 'profils' : 'profil'})
                            </span>
                        </h2>
                        <button
                            onClick={() => { setSearchTerm(''); setIsSearching(false); }}
                            className="text-sm text-[var(--color-cabaza-purple)] hover:text-[var(--color-cabaza-pink)]"
                        >
                            {t.common?.clear || 'Effacer'}
                        </button>
                    </div>

                    {searchResults.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {searchResults.map((person) => {
                                const id = person.id || person.ID;
                                const peopleUuid = person.peopleUuid || person.PEOPLEUUID;
                                return (
                                    <PeopleCard
                                        key={id}
                                        person={person}
                                        onClick={handlePeopleClick}
                                        actionType="add-remove"
                                        isAdded={isContactAdded(peopleUuid)}
                                        userEmail={userEmail}
                                        language={language}
                                        variant="dashboard"
                                        t={t}
                                    />
                                );
                            })}
                        </div>
                    ) : (
                        <p className="text-center text-gray-500 py-8">
                            {t.dashboard?.noResults || 'Aucun résultat trouvé'}
                        </p>
                    )}
                </div>
            )}

            {/* Stats Grid - Only show when not searching */}
            {!isSearching && (
                <>
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                        <StatCard
                            icon={Users}
                            label={t.dashboard?.contacts || 'Contacts'}
                            value={stats.contacts}
                            color="bg-[var(--color-cabaza-purple)]"
                            onClick={() => onNavigate('contacts')}
                        />
                        <StatCard
                            icon={CalendarDays}
                            label={t.dashboard?.events || 'Événements'}
                            value={stats.eventsCreated + stats.eventsParticipating}
                            color="bg-green-500"
                            onClick={() => onNavigate('events')}
                        />
                        <StatCard
                            icon={MessageSquare}
                            label={t.dashboard?.conversations || 'Conversations'}
                            value={stats.conversations}
                            color="bg-blue-500"
                            onClick={() => onNavigate('messages')}
                        />
                        <StatCard
                            icon={MapPin}
                            label={t.dashboard?.favoriteSpots || 'Endroits favoris'}
                            value={stats.favoriteSpots}
                            color="bg-orange-500"
                            onClick={() => onNavigate('places')}
                        />
                    </div>

                    {/* Two columns: Upcoming Events & Discovery */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Upcoming Events */}
                        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <Calendar className="w-5 h-5 text-[var(--color-cabaza-purple)]" />
                                    {t.dashboard?.upcomingEvents || 'Événements à venir'}
                                </h2>
                                <button
                                    onClick={() => onNavigate('calendar')}
                                    className="text-sm text-[var(--color-cabaza-purple)] hover:text-[var(--color-cabaza-pink)] flex items-center gap-1"
                                >
                                    {t.common?.viewAll || 'Voir tout'}
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>

                            {upcomingEvents.length > 0 ? (
                                <div className="space-y-3">
                                    {upcomingEvents.map((event, index) => (
                                        <div
                                            key={event.id || index}
                                            className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                                            onClick={() => onNavigate('events')}
                                        >
                                            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: 'var(--color-cabaza-purple)', opacity: 0.1 }}>
                                                <CalendarDays className="w-5 h-5 text-[var(--color-cabaza-purple)]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="font-medium text-gray-900 truncate">
                                                    {event.title || event.name}
                                                </p>
                                                <p className="text-sm text-gray-500">
                                                    {formatEventDate(event.startDate || event.start_date)}
                                                </p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="text-center py-8">
                                    <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                    <p className="text-gray-500">
                                        {t.dashboard?.noUpcomingEvents || 'Aucun événement à venir'}
                                    </p>
                                    <button
                                        onClick={() => onNavigate('events')}
                                        className="mt-2 text-sm text-[var(--color-cabaza-purple)] hover:text-[var(--color-cabaza-pink)]"
                                    >
                                        {t.dashboard?.createEvent || 'Créer un événement'}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Discovery Profile */}
                        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                            <div className="flex items-center justify-between mb-4">
                                <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                                    <Sparkles className="w-5 h-5 text-amber-500" />
                                    {t.dashboard?.discover || 'Découverte'}
                                </h2>
                                <button
                                    onClick={loadDashboardData}
                                    className="text-sm text-[var(--color-cabaza-purple)] hover:text-[var(--color-cabaza-pink)] flex items-center gap-1"
                                >
                                    <TrendingUp className="w-4 h-4" />
                                    {t.dashboard?.newProfile || 'Autre profil'}
                                </button>
                            </div>

                            {discoveryProfile ? (
                                <PeopleCard
                                    person={discoveryProfile}
                                    onClick={handlePeopleClick}
                                    actionType="add-remove"
                                    isAdded={isContactAdded(discoveryProfile.peopleUuid || discoveryProfile.PEOPLEUUID)}
                                    userEmail={userEmail}
                                    language={language}
                                    variant="dashboard"
                                    t={t}
                                />
                            ) : (
                                <div className="text-center py-8">
                                    <User className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                                    <p className="text-gray-500">
                                        {t.dashboard?.noProfilesYet || 'Aucun profil à découvrir pour le moment'}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Quick Actions */}
                    <div className="bg-gradient-to-r from-gray-50 to-gray-100 rounded-xl p-5">
                        <h2 className="text-lg font-semibold text-gray-900 mb-4">
                            {t.dashboard?.quickActions || 'Actions rapides'}
                        </h2>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                            <button
                                onClick={() => onNavigate('search')}
                                className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl hover:shadow-md transition-all"
                            >
                                <Search className="w-6 h-6 text-[var(--color-cabaza-purple)]" />
                                <span className="text-sm text-gray-700">{t.dashboard?.searchProfiles || 'Rechercher'}</span>
                            </button>
                            <button
                                onClick={() => onNavigate('map')}
                                className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl hover:shadow-md transition-all"
                            >
                                <MapPin className="w-6 h-6 text-green-500" />
                                <span className="text-sm text-gray-700">{t.dashboard?.viewMap || 'Voir la carte'}</span>
                            </button>
                            <button
                                onClick={() => onNavigate('profile')}
                                className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl hover:shadow-md transition-all"
                            >
                                <User className="w-6 h-6 text-blue-500" />
                                <span className="text-sm text-gray-700">{t.dashboard?.myProfile || 'Mon profil'}</span>
                            </button>
                            <button
                                onClick={() => onNavigate('modules')}
                                className="flex flex-col items-center gap-2 p-4 bg-white rounded-xl hover:shadow-md transition-all"
                            >
                                <Star className="w-6 h-6 text-amber-500" />
                                <span className="text-sm text-gray-700">{t.dashboard?.modules || 'Modules'}</span>
                            </button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default Dashboard;
